import {useEffect, useState} from "react";
import * as React from "react";

import {KeypadContext} from "@khanacademy/keypad-context";
import type {StyleType} from "@khanacademy/wonder-blocks-core";
import type {MediaSize} from "@khanacademy/wonder-blocks-layout";
import {publishAnalyticsEventForMinorConversion} from "@repo/analytics-learning-time";
import {useCoeditorContext} from "@repo/guide-coeditor-core";
import {
    isGuideChatDisabled,
    useCloseThreadOnUnload,
    useGuideMessageEventHandling,
    useInitializeGuide,
    useOpenThenSendChatMessageWhenInitialized,
} from "@repo/guide-core";
import {GuideStateApi, useGuideState} from "@repo/guide-state";
import {useLocation} from "@repo/urls";

import {HEADER_HEIGHT} from "../header/constants.ts";

const CONTENT_FOOTER_HEIGHT = 66;

export interface KhanmigoChatUIProps {
    swagId?: string;
    mediaSize: MediaSize;
    open: boolean;
    expanded: boolean;
    isGuideOffline: boolean;
    isCoeditorOpen: boolean;
    showMenu: boolean;
    showPreferences: boolean;
    shouldHideToggleAndNotifications: boolean;
    onEscapeKey: (_: React.KeyboardEvent) => void;
    toggleChatWindow: () => void;
    toggleExpanded: () => void;
    toggleMenu: () => void;
    togglePreferences: () => void;
    responsiveContainerStyle: StyleType;
}

interface Props {
    mediaSize: MediaSize;
    component: (props: KhanmigoChatUIProps) => React.ReactElement | null;
}

/**
 * A component to contain the state of the Khanmigo chat window UI.
 *
 * This is separate from the legacy popover so that it can support both the
 * popover and the new Khanmigo sidebar UI.
 */
export const GuideChatWindowState = ({mediaSize, component}: Props) => {
    const {search, pathname} = useLocation();
    const chatDisabled = isGuideChatDisabled(pathname);
    const initializeGuide = useInitializeGuide();
    const openThenSendChatMessageWhenInitialized =
        useOpenThenSendChatMessageWhenInitialized();

    const initialized = useGuideState("initialized");
    const isOffline = useGuideState("isOffline");
    const navigation = useGuideState("navigation");
    const open = useGuideState("open");
    const swagId = useGuideState("swagId");
    const threadId = useGuideState("threadId");

    const [expanded, setExpanded] = useState(false);
    const [showMenu, setShowMenu] = useState(false);
    const [showPreferences, setShowPreferences] = useState(false);
    const {open: isCoeditorOpen} = useCoeditorContext();
    const {keypadActive} = React.useContext(KeypadContext);
    const [khanmigoContainerTop, setKhanmigoContainerTop] =
        useState(HEADER_HEIGHT);
    useGuideMessageEventHandling();
    useCloseThreadOnUnload();

    useEffect(() => {
        // Wait until teacher khanmigo access has loaded to initialize.
        if (initialized || chatDisabled) {
            return;
        }

        if (open) {
            initializeGuide();
            return;
        }

        const queryParams = new URLSearchParams(search);
        const khanmigoParam = queryParams.get("khanmigo");
        if (khanmigoParam != null) {
            // It looks like khanmigoParam is the text that we expect to be
            // sent to the chat when it initializes. Currently, the "khanmigo"
            // search param appears to only be powering an action bubble.
            // See services/ai-guide/chat/modifier_helpers/links.go
            openThenSendChatMessageWhenInitialized(khanmigoParam, {
                containsSpeechToTextContent: false,
                guideVoiceDescriptor: null,
                requestUIType: "ACTION_BUBBLE",
                prepopulatedText: khanmigoParam,
                requestWithTextToSpeech: false,
                timeToSubmit: 0,
            });
        }
    }, [
        initialized,
        initializeGuide,
        open,
        chatDisabled,
        search,
        openThenSendChatMessageWhenInitialized,
    ]);

    const handleEscapeKey = (event: React.KeyboardEvent) => {
        if (open && event.key === "Escape") {
            toggleChatWindow();
        }
    };

    const toggleChatWindow = () => {
        GuideStateApi.set("open", !open);
        if (open) {
            publishAnalyticsEventForMinorConversion({
                aiGuideThreadId: threadId ?? "",
                aiGuideNavigationId: navigation.id,
                eventSchemaVersion: 1,
                eventType: "AiGuideChatWindowClosed",
            });
        } else {
            publishAnalyticsEventForMinorConversion({
                aiGuideThreadId: threadId ?? "",
                aiGuideNavigationId: navigation.id,
                eventSchemaVersion: 1,
                eventType: "AiGuideChatWindowOpened",
            });
        }
    };

    // Don't render the close button for small or medium screens.
    // We expect the user to close Khanmigo using the close button
    // in the header. We also don't want to render the toggle over the
    // Perseus keypad.
    const shouldHideToggleAndNotifications =
        (open && ["small", "medium"].includes(mediaSize)) || keypadActive;

    const mainContent = document.getElementById("main-content");
    if (
        mainContent &&
        khanmigoContainerTop !== mainContent.getBoundingClientRect().top
    ) {
        setKhanmigoContainerTop(mainContent.getBoundingClientRect().top);
    }
    const responsiveContainerStyle = {
        top: khanmigoContainerTop,
        height: `calc(100vh - ${
            khanmigoContainerTop + CONTENT_FOOTER_HEIGHT
        }px)`,
    };

    return component({
        swagId,
        mediaSize,
        open,
        expanded,
        isGuideOffline: isOffline,
        isCoeditorOpen,
        showMenu,
        showPreferences,
        shouldHideToggleAndNotifications,
        onEscapeKey: handleEscapeKey,
        toggleChatWindow,
        responsiveContainerStyle,
        toggleExpanded: () => setExpanded(!expanded),
        toggleMenu: () => setShowMenu(!showMenu),
        togglePreferences: () => setShowPreferences(!showPreferences),
    });
};
